import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import {
  useCurrentShopAdmin,
  useUpdateShopAdmin,
} from "@/api/shops/use-shop-admin";
import FormSwitch from "@babackapp/react-hook-forms-components/form-switch";
import useTranslation from "@babackapp/translations/use-translation";
import { Card, CardContent } from "@babackapp/ui/components/card";
import { Form, FormField } from "@babackapp/ui/components/form";
import { Skeleton } from "@babackapp/ui/components/skeleton";
import { useEffect } from "react";
import { useForm, useWatch } from "react-hook-form";
import { z } from "zod";

const ShopAdminSettingsFormSchema = z.object({
  newPortal: z.boolean(),
  demo: z.boolean(),
  pushReasonExternalIdToShopifyNote: z.boolean(),
});

type ShopAdminSettingsFormValues = z.infer<typeof ShopAdminSettingsFormSchema>;

/** Each switch reads `<translationKey>.title` and `<translationKey>.description`. */
const SWITCHES: {
  name: keyof ShopAdminSettingsFormValues;
  translationKey: string;
}[] = [
  {
    name: "newPortal",
    translationKey: "settings.shop.admin.returnWidget.enabled",
  },
  { name: "demo", translationKey: "settings.shop.admin.returnWidget.demo" },
  {
    name: "pushReasonExternalIdToShopifyNote",
    translationKey: "settings.shop.admin.pushReasonExternalIdToShopifyNote",
  },
];

function ShopAdminSettingsForm(defaultValues: ShopAdminSettingsFormValues) {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const { mutateAsync: updateShopAdmin } = useUpdateShopAdmin(shop.id);

  const form = useForm<ShopAdminSettingsFormValues>({
    mode: "onChange",
    defaultValues,
  });

  const values = useWatch({ control: form.control });

  const onSubmit = form.handleSubmit((next: ShopAdminSettingsFormValues) =>
    updateShopAdmin({
      returnWidget: { demo: next.demo, enabled: next.newPortal },
      pushReasonExternalIdToShopifyNote:
        next.pushReasonExternalIdToShopifyNote,
    }).then(() => form.reset(next)),
  );

  useEffect(() => {
    if (form.formState.isDirty) onSubmit();
  }, [values, form.formState.isDirty]);

  return (
    <Form {...form}>
      <form>
        {SWITCHES.map(({ name, translationKey }) => (
          <FormField
            key={name}
            control={form.control}
            name={name}
            render={({ field }) => (
              <FormSwitch
                label={t(`${translationKey}.title`)}
                description={t(`${translationKey}.description`)}
                {...field}
              />
            )}
          />
        ))}
      </form>
    </Form>
  );
}

/** Baback-internal settings. Render only behind `useViewerIsSuperAdmin()`. */
export default function ShopAdminSettingsCard() {
  const { data, isPending } = useCurrentShopAdmin();

  return (
    <Card className="w-full">
      <CardContent className="py-8 px-8">
        {isPending ? (
          <Skeleton className="h-10 w-full" />
        ) : (
          <ShopAdminSettingsForm
            newPortal={data?.returnWidget?.enabled ?? false}
            demo={data?.returnWidget?.demo ?? false}
            pushReasonExternalIdToShopifyNote={
              data?.pushReasonExternalIdToShopifyNote ?? false
            }
          />
        )}
      </CardContent>
    </Card>
  );
}
